/* eslint-disable import/no-cycle */
/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
/* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
*                _
*  _            | | _
* | |         _ | || |
* | |__   _  | || || |__
* | '_ \ | | | || || '_ \
* | (_) || |_| || || (_) |
*  \___/  \___/ |_| \___/
*/

import {
  Component, Input, OnChanges,
  OnInit, SimpleChanges, ViewChild,
  ElementRef,
} from '@angular/core';

import { cloneDeep, assign } from 'lodash-es';

import { NgForm } from '@angular/forms';
import { ButtonStyle } from 'src/aero/aero-button/aero-button.component';
import { AeroFormComponent } from '../../aero/aero-form/aero-form.component';
import { AeroInputComponent } from '../../aero/aero-input/aero-input.component';
import { AeroTextareaComponent } from '../../aero/aero-textarea/aero-textarea.component';
import { Resume } from '../resume.model';
import { ResumeComponent } from '../resume.component';
import { ResumeCustomSubSection } from './custom-sub-section.model';
import { ResumeSection } from '../section/section.model';
import { ResumeSubSectionComponent } from '../sub-section/sub-section.component';
import { SectionDataAlertService } from '../section/section-data-alert.service';
import { UnitSimpleDTO } from '../content-selection-modal/unit-simple-dto.model';

/**
 * Component representing custom sections which the user can create to add any
 * information that doesn't fit in our provided resume sections.
 */
@Component({
  selector: 'bulb-resume-custom-section',
  templateUrl: './custom-section.component.html',
  standalone: false,
})
export class ResumeCustomSectionComponent implements OnChanges, OnInit {
  /**
   * Whether the current user can edit the section.
   */
  @Input() canEdit: boolean;

  /**
   * Custom section model.
   */
  @Input() customSection: ResumeSection;

  /**
   * Reference to the parent resume component.
   */
  @Input() resumeComponent: ResumeComponent;

  /**
   * Reference to the author of the resume.
   */
  @Input() user;

  /**
   * Max length of custom description field
   */
  public maxLength: number = 1000;

  /**
   * Number indicating how many units a user has removed from their portfolio content.
   */
  public numberOfRemovedContent : number = 0;

  /**
   * Boolean indicating if the content ( data ) has been updated.
   */
  public contentUpdated: boolean = false;

  /**
   * Clone of the about me sub-section data to use during edit, so any changes
   * can be cancelled or saved as needed.
   */
  public customSubSectionClone: ResumeCustomSubSection;

  /**
   * Whether the section is showing the edit form or not.
   */
  public editMode: boolean = false;

  /**
   * Open/Close accordion tab
   */
  public isTabOpen: boolean = false;

  /**
   * Set the value as true if the subsection contains only portfolio content.
   */
  public isOnlyPortfolioContent : boolean = false;

  /**
   * Array of portfolio content that the user has selected.
   */
  public portfolioContent: Array<any> = new Array<any>();

  /**
   * Array of portfolio content that the user has removed.
   */
  public removedPortfolioContent : Array< UnitSimpleDTO > = new Array<UnitSimpleDTO>();

  /**
   * User's resume reference.
   */
  public resume: Resume;

  /**
   * User's chosen title for the custom section
   */
  public sectionTitle: string = '';

  /**
   * User's description for the custom section
   */
  public sectionInformation: string = '';

  /**
   * Tracks for the tab changes were made in session.
   */
  public tabChange: boolean;

  /**
   * custom tab opening/closing status.
   */
  customTabOpen: string;

  /**
   * Boolean indicating if the user has saved the data in this section.
   * @private
   */
  private hasSaved: boolean = false;

  /**
   * Number indicating how many units a user has selected to display in
   * their portfolio content.
   * @private
   */
  private numberOfSelectedContent: number = 0;

  /**
   * An Array of the initial values in the aero form component. It is an array, since other sections
   * (Experience && Education) have an aeroFormComponentList which is an array.
   *  For Custom section there would only ever be
   *  one entry.
   * @private
   */
  private initialAeroFormValuesArray : Array<any> = new Array<any>();

  /**
   * Button style for aero button
   */
  public buttonStyle : ButtonStyle = ButtonStyle.PRIMARY_2;

  /**
   * flag for character limit reached.
   */
  public charLimitReached: boolean = false;

  /**
   * Reference to the AeroFormComponent in the custom section resume sub-section.
   */
  @ViewChild(AeroFormComponent) public aeroFormComponent: AeroFormComponent;

  /**
   * Reference to the AeroInputComponent in the custom section resume sub-section.
   */
  @ViewChild(AeroInputComponent) aeroInputComponent: AeroInputComponent;

  /**
   * Reference to the AeroTextareaComponent in the custom section resume sub-section.
   */
  @ViewChild(AeroTextareaComponent) aeroTextareaComponent: AeroTextareaComponent;

  /**
   * Reference to the ResumeSubSectionComponent in the custom section resume sub-section.
   */
  @ViewChild(ResumeSubSectionComponent) subSectionComponent: ResumeSubSectionComponent;

  /**
   * Default Constructor.
   *
   * @param elementRef
   * @param sectionDataAlertService
   */
  constructor(
    private elementRef : ElementRef,
    private sectionDataAlertService : SectionDataAlertService,
  ) {}

  /**
   * A callback method that is invoked immediately after the default change detector has checked the directive's
   * data-bound properties for the first time, and before any of the view or content children have been checked.
   */
  public ngOnInit(): void {
    this.cloneSubSection();
    this.initializeFields();
    if (this.customSection.empty && this.canEdit) {
      this.beginEdit();
    }
    if (this.tabChange) {
      this.isTabOpen = JSON.parse(sessionStorage.getItem(this.customTabOpen));
    } else if (!this.customSection.empty && !this.tabChange) {
      this.isTabOpen = true;
    } else {
      sessionStorage[this.customTabOpen] = false;
    }
  }

  /**
   * Handle adding the user's potential portfolio content to custom section.
   */
  public addPortfolioContent(): void {
    this.subSectionComponent.addPortfolioContent(this);
  }

  /**
   * Helper method to update the portoFolioContent object identity so that the Angular detection cycle could detect
   * the object change after the removal of any portfolio content.
   *
   * @param event
   * @private
   */
  public onPortfolioContentRemoved() : void {
    this.subSectionComponent.removePortfolioContent(this);
  }

  /**
   * Response handler for edit event from section component which moves us into 'edit' mode.
   */
  public beginEdit() {
    this.cloneSubSection();
    this.isTabOpen = true;
    sessionStorage[this.customTabOpen] = true;
    this.editMode = true;
    setTimeout(() => {
      this.sectionDataAlertService.enableAlert(this.aeroFormComponent, () => { this.cancelEdit(); });
    });
  }

  /**
   * Cancel button handler.
   */
  public cancelEdit() {
    if (this.customSection.empty) {
      this.isTabOpen = false;
      sessionStorage[this.customTabOpen] = false;
    } else {
      this.editMode = false;
    }
    this.hasSaved = false;
    this.handleUnsavedPortfolioContent();
    this.aeroFormComponent.markAeroFormAsPristine();
    this.aeroFormComponent.updateSaveButtonState();
    this.sectionDataAlertService.unregisterBeforeUnloadEvent();
    this.sectionDataAlertService.disableAlert();
    this.cloneSubSection();

    if (this.customSection.empty) {
      this.resumeComponent.resume.sections.pop();
      this.resumeComponent.addComponent(true);
    }
  }

  /**
   * Method to clone the existing CustomSubSection, or create a new one if one doesn't exist.
   * @private
   */
  private cloneSubSection() {
    if (this.customSection.subSections && this.customSection.subSections[0]) {
      this.customSubSectionClone = <ResumeCustomSubSection>cloneDeep(this.customSection.subSections[0]);
      this.updateIsisOnlyPortfolioContent();
    } else {
      this.customSubSectionClone = new ResumeCustomSubSection();
    }
  }

  /**
   * Callback method, called when field content updated.
   * @param event
   */
  public contentChangeHandler() {
    this.contentUpdated = true;
  }

  /**
   * Handle closing of the accordion on the header tab click.
   */
  handleTabClose(): void {
    this.cloneSubSection();
    this.handleUnsavedPortfolioContent();
    this.aeroFormComponent.resetAeroFormToInitialValues(this.customSubSectionClone);
    this.isTabOpen = false;
    sessionStorage[this.customTabOpen] = false;
    this.editMode = false;
    this.sectionDataAlertService.unregisterBeforeUnloadEvent();
    this.sectionDataAlertService.disableAlert();
  }

  /**
   * Handles open of tab
   */
  handleTabOpen() {
    this.isTabOpen = true;
    sessionStorage[this.customTabOpen] = true;
  }

  /**
   * Method that handles removing any unsaved portfolio content from the view.
   *
   * @private
   */
  private handleUnsavedPortfolioContent(): void {
    this.subSectionComponent.handleUnsavedPortfolioContent(this);
  }

  /**
   * Method to pre populate fields.
   */
  public initializeFields() {
    if (this.aeroFormComponent) {
      this.aeroFormComponent.form.resetForm(this.aeroFormComponent.form.value);
    }
    this.portfolioContent = this.customSubSectionClone.portfolioContent;
  }

  /**
   * Method to save the Custom information.
   *
   * @param resumeCustomInfo
   */
  public save(resumeCustomInfo: NgForm) {
    // Update the clone with the attached portfolio content
    const portfolioContentDTO: Array<any> = new Array<any>();
    this.portfolioContent.forEach((unit) => {
      const unitDTO: any = {
        id: unit.id,
      };
      portfolioContentDTO.push(unitDTO);
    });
    this.customSubSectionClone.portfolioContent = portfolioContentDTO;
    this.editMode = false;

    // Assign the changes from the clone into the real section/subsection.
    if (this.customSection.subSections && this.customSection.subSections[0]) {
      assign(this.customSection.subSections[0], this.customSubSectionClone);
    } else {
      this.customSection.subSections = new Array<ResumeCustomSubSection>();
      this.customSection.subSections.push(this.customSubSectionClone);
    }

    this.customSection.empty = false;
    this.resumeComponent.save().then((updatedResume) => {
      this.resumeComponent.resume.sections.slice(-1)[0].id = updatedResume.sections.slice(-1)[0].id;
      const index = this.resumeComponent.resume.sections.map((section : ResumeSection) => section.id)
        .indexOf(this.customSection.id);
      assign(this.customSection.subSections[0], updatedResume.sections[index].subSections[0]);
      this.portfolioContent = updatedResume.sections[index].subSections[0].portfolioContent;
      this.hasSaved = true;
      this.updateIsisOnlyPortfolioContent();
      resumeCustomInfo.resetForm(resumeCustomInfo.value);
      this.editMode = false;
    });
    this.aeroFormComponent.markAeroFormAsPristine();
    this.aeroFormComponent.updateSaveButtonState();
    // Update the initial value of the aero form component with the new data:
    this.initialAeroFormValuesArray[0] = resumeCustomInfo.form.value;
    this.sectionDataAlertService.resetPortfolioContentModifiedMap();
    this.sectionDataAlertService.disableAlert();
    this.sectionDataAlertService.unregisterBeforeUnloadEvent();
  }

  /**
   * Called when @Input parameter for this component is updated.
   * @param changes
   */
  public ngOnChanges(): void {
    this.cloneSubSection();
    this.initializeFields();
  }

  /**
   * Check and update if the sub section contains only portfolio content.
   * @private
   */
  private updateIsisOnlyPortfolioContent() {
    this.isOnlyPortfolioContent = !(this.customSubSectionClone.customDescription);
  }

  /**
   * Callback method which is called when editor data is changed.
   * @param customDescription
   */
  updateCustomData(customDescription) {
    this.customSubSectionClone.customDescription = customDescription;
    this.sectionDataAlertService.handleSaveOnContentUpdate(this.aeroFormComponent, this.charLimitReached);
  }

  /**
   * Callback function when input model changes
   * @param $event
   * @param subSectionIndex
   */
  handleInputModelChanges($event) {
    this.sectionDataAlertService.handleSaveOnContentUpdate(this.aeroFormComponent, this.charLimitReached);
  }

  /**
   * Callback function for character count.
   * @param charCount - current number of character in the editor.
   */
  handleLimitReached(charCount) {
    this.charLimitReached = (charCount > this.maxLength);
  }

  /**
   *  Drag start function.
   * @param event event
   * @param section Dragged section
   */
  dragStart(event, section) {
    this.resumeComponent.dragStart(section);
  }

  /**
   * Drop function
   * @param event event
   * @param section  On dragged section
   */
  drop(event, section) {
    this.resumeComponent.drop(section);
  }
}
